import { memo as Memo, useRef, useState, useMemo, useEffect } from 'react';
import Map, { Marker, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

//* HOC's
import { withUIContext } from 'context';

//* Components
import CategoryNavbar from './CategoryNavbar';
import LocationNavbar from './LocationNavbar';
import InfoNavbar from './InfoNavbar';
import Text from 'components/common/Text';
import Image from 'components/common/Image';

//* Styles
import MapStyle from './style';

const MapboxMap = Memo(({ categories, top_destinations, regions, winWidth }) => {
	//! Refs
	const mapRef = useRef();
	const preMapInfo = useRef({
		lat: null,
		lng: null,
		zoom: null,
	});

	const defaultCoordinates = useRef({
		lng: 44.50349,
		lat: 40.1772,
	});

	//! States
	const [isFiltersOpen, setIsFiltersOpen] = useState(true);
	const [activeCategory, setActiveCategory] = useState();
	const [activeRegion, setActiveRegion] = useState();
	const [activeInfo, setActiveInfo] = useState();
	const [isFromMarker, setIsFromMarker] = useState(false);
	const [activeSubCategories, setActiveSubCategories] = useState([]);

	//! Markers
	const [markers, filteredCategories] = useMemo(() => {
		let cats = activeCategory ? [activeCategory] : categories;

		cats = activeSubCategories.length ? activeSubCategories : cats;

		cats = activeRegion ? cats.map((cat) => ({ ...cat, locations: cat.locations.filter((loc) => loc.region_id === activeRegion.id) })) : cats;
		cats = activeInfo ? cats.map((cat) => ({ ...cat, locations: cat.locations.filter((loc) => loc.id === activeInfo.id) })) : cats;

		const markers = cats.reduce((arr, cat) => {
			return [
				...arr,
				...cat.locations.map((location, k) => {
					const lng = Number.isNaN(+location.longitude) ? false : +location.longitude;
					const lat = Number.isNaN(+location.latitude) ? false : +location.latitude;

					(!lng || !lat) && console.error(`${location.name} coordinates not valid (${location.longitude}, ${location.latitude})`);
					return (
						lng &&
						lat && (
							<Marker
								key={cat.name + k}
								anchor='bottom'
								longitude={lng}
								latitude={lat}
							>
								<div
									className={`marker-container bg${cat.color}`}
									onClick={() => {
										setActiveInfo({
											...location,
											cat,
										});
										setIsFiltersOpen(true);
										setIsFromMarker(true);
									}}
								>
									<Image
										className='marker-icon'
										objectFit={'contain'}
										src={location.category.icon_hover?.url}
										alt={location.category.icon?.alt}
									/>
									<Image
										className='marker-hover-icon'
										objectFit={'contain'}
										src={location.category.icon?.url}
										alt={location.category.icon?.alt}
									/>
									<div className='marker-hover-cont'>
										<div className='marker-hover-inner-cont'>
											<div className='marker-hover-image'>
												<Image
													src={location.image.url}
													alt={location.image.alt}
												/>
											</div>

											<div className='marker-hover-info'>
												<Text
													text={location.name}
													className='marker-hover-title font-ping-bold'
												/>
												<Text
													text={location.region_name}
													className='secondaryColor2 p pXS'
												/>
											</div>
										</div>
									</div>
									<span className='marker-hover-triangle' />
								</div>
							</Marker>
						)
					);
				}),
			];
		}, []);

		return [markers, cats];
	}, [categories, activeCategory, activeRegion, activeInfo, activeSubCategories]);

	//! Did Mount
	useEffect(() => {
		setIsFiltersOpen(window.innerWidth >= 1280);
	}, []);

	//! Update
	useEffect(() => {
		if (activeInfo) {
			preMapInfo.current = {
				...mapRef.current.getCenter(),
				zoom: mapRef.current.getZoom(),
			};

			const lng = Number.isNaN(+activeInfo.longitude) ? false : +activeInfo.longitude;
			const lat = Number.isNaN(+activeInfo.latitude) ? false : +activeInfo.latitude;

			if (lng && lat) {
				mapRef.current.setCenter([activeInfo.longitude, activeInfo.latitude]);
				mapRef.current.setZoom(12);
			}
		} else if (mapRef.current) {
			mapRef.current.setCenter([preMapInfo.current.lng || defaultCoordinates.current.lng, preMapInfo.current.lat || defaultCoordinates.current.lat]);
			mapRef.current.setZoom(preMapInfo.current.zoom || 9);
		}
	}, [activeInfo]);

	//! Resize Map
	useEffect(() => {
		mapRef.current?.resize();
	}, [isFiltersOpen]);

	//! Scroll To top
	useEffect(() => {
		window.scrollTo({ top: 0 });
	}, [activeCategory, activeInfo]);

	return (
		<>
			<MapStyle className={`map-container ${isFiltersOpen ? '' : 'closed'}`}>
				<div className='map-info-container'>
					<div className='map-info-inner-container'>
						<CategoryNavbar
							categories={categories}
							top_destinations={top_destinations}
							selectCategory={setActiveCategory}
							selectInfo={setActiveInfo}
							active={!activeCategory && !activeInfo}
						/>

						<LocationNavbar
							categories={categories}
							regions={regions}
							selectCategory={setActiveCategory}
							selectedCategory={activeCategory}
							selectSubCategory={setActiveSubCategories}
							selectedSubCategory={activeSubCategories}
							selectRegion={setActiveRegion}
							selectedRegion={activeRegion}
							filteredCategories={filteredCategories}
							selectInfo={setActiveInfo}
							clearFilter={() => {
								setActiveCategory(null), setActiveSubCategories([]);
								setActiveInfo(null), setActiveRegion(null);
							}}
						/>

						<InfoNavbar
							selectedInfo={activeInfo}
							selectedCategory={activeCategory}
							clearInfo={() => {
								if (winWidth < 768 && isFromMarker) {
									setIsFromMarker(false);
									setIsFiltersOpen(false);
								}
								setActiveInfo(null);
							}}
						/>
					</div>
				</div>

				<div
					className={`burger-icon ${isFiltersOpen ? 'active' : ''}`}
					onClick={() => setIsFiltersOpen(!isFiltersOpen)}
				/>

				<Map
					ref={mapRef}
					attributionControl={true}
					mapboxAccessToken={process.env.MAPBOX_TOKEN}
					mapStyle={process.env.MAPBOX_STYLE || 'mapbox://styles/mapbox/streets-v11'}
					initialViewState={{
						longitude: defaultCoordinates.current.lng,
						latitude: defaultCoordinates.current.lat,
						zoom: 9,
					}}
					// scrollZoom={false}
					// touchZoomRotate={false}
					// dragPan={false}
				>
					{markers}

					<NavigationControl />
				</Map>
			</MapStyle>
		</>
	);
});

export default withUIContext(MapboxMap, ['winWidth']);
